import Pica from 'pica';

export interface CompressImageOptions {
  /** Maximum width in pixels the compressed image may reach. Aspect ratio preserved. */
  maxWidth?: number;
  /** Maximum height in pixels the compressed image may reach. Aspect ratio preserved. */
  maxHeight?: number;
  /** Pica resize quality (0–3). Higher is better but slower. Default 3. */
  quality?: 0 | 1 | 2 | 3;
  /** Output MIME type. Defaults to the input file's type. */
  outputType?: string;
}

const DEFAULT_MAX_DIMENSION = 1920;

const getScaledDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
) => {
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }
  const ratio = Math.min(maxWidth / originalWidth, maxHeight / originalHeight);
  return {
    width: Math.max(1, Math.round(originalWidth * ratio)),
    height: Math.max(1, Math.round(originalHeight * ratio))
  };
};

/**
 * Compress and resize an image `File` in the browser using Pica.
 *
 * Aspect ratio is preserved. If the source is already within the target
 * dimensions it is still re-encoded through Pica for consistent output.
 * On any failure the original file is returned so callers can safely
 * wrap this in an upload pipeline without extra try/catch.
 */
export const compressImage = async (
  file: File,
  options: CompressImageOptions = {}
): Promise<File> => {
  const {
    maxWidth = DEFAULT_MAX_DIMENSION,
    maxHeight = DEFAULT_MAX_DIMENSION,
    quality = 3,
    outputType
  } = options;

  const type = outputType || file.type;
  const pica = Pica({ features: ['wasm', 'ww'] });

  try {
    const imageBitmap = await createImageBitmap(file);
    const { width, height } = getScaledDimensions(
      imageBitmap.width,
      imageBitmap.height,
      maxWidth,
      maxHeight
    );

    const sourceCanvas = new OffscreenCanvas(
      imageBitmap.width,
      imageBitmap.height
    );
    const sourceCtx = sourceCanvas.getContext(
      '2d'
    ) as unknown as CanvasRenderingContext2D | null;
    if (!sourceCtx) {
      throw new Error('Failed to acquire 2D context for compression source.');
    }
    sourceCtx.drawImage(imageBitmap, 0, 0);

    const targetCanvas = new OffscreenCanvas(width, height);
    await pica.resize(sourceCanvas, targetCanvas, { quality });

    // @ts-ignore — OffscreenCanvas.convertToBlob is not in every lib.dom variant
    const blob: Blob = await targetCanvas.convertToBlob({ type });

    return new File([blob], file.name, {
      type,
      lastModified: Date.now()
    });
  } catch (error) {
    console.error('compressImage failed, returning original file:', error);
    return file;
  }
};

const IMAGE_MIME_PREFIX = 'image/';

/** True when a `File` is an image type that `compressImage` should handle. */
export const isCompressibleImage = (file: File): boolean =>
  file.type.startsWith(IMAGE_MIME_PREFIX);

/**
 * Convenience wrapper for the common "run compressImage on every image in a
 * batch, pass through anything else" pattern used by upload flows.
 */
export const compressImages = async (
  files: File[],
  options?: CompressImageOptions
): Promise<File[]> =>
  Promise.all(
    files.map((file) =>
      isCompressibleImage(file) ? compressImage(file, options) : file
    )
  );
